import React from 'react';
import {withTranslation} from 'react-i18next';
import {startOfDay, endOfDay} from 'date-fns';
import styled from 'styled-components';

import {Btn} from '../../../../shared/elements/btn/Btn';
import {Card} from '../../../../shared/elements/card/Card';
import IconContainer, {Icon} from '../../../../shared/elements/icon/Icon';
import Table, {StripedTable} from '../../../../shared/elements/table/TableFeature';
import {FormDetail} from '../../../../shared/form/form-control/FormDetail';
import {FormLabel} from '../../../../shared/form/form-features/FormLabel';
import {formatDate} from '../../../../util/date';

import useCustomer from './../CustomerState.js';

function CustomerInfoTabView({selectedCustomer, t, setEditCustomerModal}) {
    const {customer, loading} = useCustomer(selectedCustomer.customerId, t);
    if (loading) return <span>{t('general.Loading')}</span>;

    const labelSaturday = <>{t('customer.Saturday')}</>;
    const labelSunday = <>{t('customer.Sunday')}</>;

    const generateLaborData = {
        laborSupervisor: [
            {label: labelSaturday, data: <>{customer.priceLaborSaturdaySupervisor}</>},
            {label: labelSunday, data: <>{customer.priceLaborSundayHolidaySupervisor}</>},
        ],
        forklift: [
            {label: labelSaturday, data: <>{customer.priceLaborSaturdayForklift}</>},
            {label: labelSunday, data: <>{customer.priceLaborSundayHolidayForklift}</>},
        ],
        surchargeInHandling: [
            {label: labelSaturday, data: <>{customer.surchargeInHandlingSat}</>},
            {label: labelSunday, data: <>{customer.surchargeInHandlingSun}</>},
        ],
        surchargeOutHandling: [
            {label: labelSaturday, data: <>{customer.surchargeOutHandlingSat}</>},
            {label: labelSunday, data: <>{customer.surchargeOutHandlingSun}</>},
        ],
    };

    return (
        <Wrapper>
            <div>
                <label>{customer.name}</label>
                <EditBtn link color="primary" onClick={() => setEditCustomerModal(customer.customerId)}>
                    <Icon icon="edit" />
                </EditBtn>
            </div>

            <DetailContainer>
                <FormDetail label={t('customer.PlantName')}>{customer.plantName || '-'}</FormDetail>
                <FormDetail label={t('customer.InvoiceLogicType')}>{customer.invoiceLogicType}</FormDetail>
            </DetailContainer>
            <CardDataWrapper>
                <CardData header={t('customer.PriceLaborForklift')}>
                    {generateLaborData.forklift.map((item, index) => (
                        <CardDataContent icon="euro" key={index} label={item.label} data={item.data} />
                    ))}
                </CardData>
                <CardData header={t('customer.PriceLaborSupervisor')}>
                    {generateLaborData.laborSupervisor.map((item, index) => (
                        <CardDataContent icon="euro" key={index} label={item.label} data={item.data} />
                    ))}
                </CardData>

                <CardData header={t('customer.SurchargeInHandling')}>
                    {generateLaborData.surchargeInHandling.map((item, index) => (
                        <CardDataContent icon="percent" key={index} label={item.label} data={item.data} />
                    ))}
                </CardData>

                <CardData header={t('customer.SurchargeOutHandling')}>
                    {generateLaborData.surchargeOutHandling.map((item, index) => (
                        <CardDataContent icon="percent" key={index} label={item.label} data={item.data} />
                    ))}
                </CardData>
            </CardDataWrapper>

            <label>{t('customer.ArticleGroups')}</label>

            <StripedTable hover>
                <thead>
                    <Table.Row>
                        <Table.Header>{t('customer.ArticleGroup.Name')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.Type')}</Table.Header>
                        <Table.Header>{t('customer.StartDate')}</Table.Header>
                        <Table.Header>{t('customer.EndDate')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.FixedQuantity')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.VariableQuantity')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.PricePerPalletFixed')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.PricePerPalletVariable')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.PricePerPalletHandlingIn')}</Table.Header>
                        <Table.Header>{t('customer.ArticleGroup.PricePerPalletHandlingOut')}</Table.Header>
                    </Table.Row>
                </thead>
                <tbody>
                    {(customer.articleGroups == null || customer.articleGroups?.length === 0) && (
                        <Table.Row>
                            <Table.Data colSpan="999">{t('customer.ArticleGroup.NoArticleGroupFound')}</Table.Data>
                        </Table.Row>
                    )}
                    {customer.articleGroups?.length > 0 &&
                        customer.articleGroups.map((ag, index) => (
                            <Table.Row key={index}>
                                <Table.Data>{ag.name}</Table.Data>
                                <Table.Data>
                                    {ag.articleGroupType === 'Default'
                                        ? t('customer.ArticleGroup.Other')
                                        : ag.articleGroupType}
                                </Table.Data>
                                <Table.Data>{formatDate(ag.startDate, 'dd-MM-yyyy H:mm', startOfDay)}</Table.Data>
                                <Table.Data>{formatDate(ag.endDate, 'dd-MM-yyyy H:mm', endOfDay)}</Table.Data>
                                <Table.Data>{ag.fixedQuantity}</Table.Data>
                                <Table.Data>{ag.variableQuantity}</Table.Data>
                                <Table.Data>
                                    <Icon icon="euro" />
                                    {ag.pricePerPalletFixed}
                                </Table.Data>
                                <Table.Data>
                                    <Icon icon="euro" />
                                    {ag.pricePerPalletVariable}
                                </Table.Data>
                                <Table.Data>
                                    <Icon icon="euro" />
                                    {ag.pricePerPalletHandlingIn}
                                </Table.Data>
                                <Table.Data>
                                    <Icon icon="euro" />
                                    {ag.pricePerPalletHandlingOut}
                                </Table.Data>
                            </Table.Row>
                        ))}
                </tbody>
            </StripedTable>

            <label>{t('customer.ExtraWorkItems')}</label>

            <StripedTable hover>
                <thead>
                    <Table.Row>
                        <Table.Header>{t('customer.ExtraWorkItem.Description')}</Table.Header>
                        <Table.Header>{t('customer.StartDate')}</Table.Header>
                        <Table.Header>{t('customer.EndDate')}</Table.Header>
                        <Table.Header>{t('customer.ExtraWorkItem.PricePerUnit')}</Table.Header>
                        <Table.Header>{t('customer.ExtraWorkItem.Unit')}</Table.Header>
                    </Table.Row>
                </thead>
                <tbody>
                    {(customer.extraWorkItems == null || customer.extraWorkItems?.length === 0) && (
                        <Table.Row>
                            <Table.Data colSpan="999">{t('customer.ExtraWorkItem.NoExtraWorkItemsFound')}</Table.Data>
                        </Table.Row>
                    )}
                    {customer.extraWorkItems?.length > 0 &&
                        customer.extraWorkItems.map((ew, index) => (
                            <Table.Row key={index}>
                                <Table.Data>{ew.description}</Table.Data>
                                <Table.Data>{formatDate(ew.startDate, 'dd-MM-yyyy H:mm', startOfDay)}</Table.Data>
                                <Table.Data>{formatDate(ew.endDate, 'dd-MM-yyyy H:mm', endOfDay)}</Table.Data>
                                <Table.Data>
                                    <Icon icon="euro" />
                                    {ew.pricePerUnit}
                                </Table.Data>
                                <Table.Data>{ew.unit}</Table.Data>
                            </Table.Row>
                        ))}
                </tbody>
            </StripedTable>
        </Wrapper>
    );
}
const EditBtn = styled(Btn)`
    padding: 0 12px;
`;
const Wrapper = styled.div`
    ${Table.Data} ${IconContainer} svg {
        width: 12px;
        padding: 1px 0 3px;
    }
`;

const CardData = ({children, header}) => (
    <Card header={header} size="small">
        <table>
            <tbody>{children}</tbody>
        </table>
    </Card>
);

const CardDataContent = ({label, icon, data}) => (
    <tr>
        <td>
            <FormLabel>{label}</FormLabel>
        </td>
        <td>
            <Icon icon={icon} />
            {data}
        </td>
    </tr>
);

const DetailContainer = styled.div`
    *,
    label {
        font-size: 12px;
        font-weight: 400;
    }
`;

const CardDataWrapper = styled.div`
    display: flex;
    gap: var(--space-lg);
    padding-top: var(--space-lg);
    flex-wrap: wrap;
    * {
        font-size: 10px;
        font-weight: 700;
    }

    table {
        * {
            font-size: 12px;
            font-weight: 400;
            margin: 0;
        }
        td {
            vertical-align: middle;
            padding: 0;

            &:first-of-type {
                padding: 0 14px 0 0;
            }
        }
    }
`;

export default withTranslation()(CustomerInfoTabView);
